import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Link from 'next/link';
import NoSSR from 'react-no-ssr';
import { Rhytm } from 'planoplan-ui-kit/core/helpers/rhytm';
import { getImage } from 'planoplan-ui-kit/core/get_image';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { ILogo } from 'planoplan-ui-kit/core/icons/i-logo';
import { handleClickDownload, downloadPlanoplanContextHandler, downloadPlanoplanAuxHandler } from '@modules/helper';
import { MAIN_MENU, SOCIAL_MENU, LEGAL_DOCS_MENU, SOFTWARE_MENU } from '@constants/menu';
import { Points, zIndex, textSize } from '@styles';
import { CustomLink } from '@components/Custom-Link';
import { useTranslations } from '@libs/translations';

const date = new Date();
const copyrightDate = date.getFullYear();
const HALF_A_LIST = 2;
const MENU_COLUMN_ITEM_DIFFERENCE = 2;
const TABLET_CONTENT_WIDTH = '968px';
const DESKTOP_CONTENT_WIDTH = '1280px';
const LAPTOP_CONTENT_WIDTH = '1180px';
const FULLHD_CONTENT_WIDTH = '1386px';

export const Footer = () => {
  const translations = useTranslations();
  const [isInnerPage, setIsInnerPage] = useState(false);
  const [isLaptop, setIsLaptop] = useState(false);
  const [isFullhd, setIsFullhd] = useState(false);
  const { locale } = useSelector((state) => state.settings);
  const { pathname } = useRouter();
  const mainMenu = MAIN_MENU.filter((i) => i.locale.includes(locale) && !i.isHideInFooter?.includes(locale));
  const softwareMenu = SOFTWARE_MENU;
  const legalDocsMenu = LEGAL_DOCS_MENU.filter((i) => i.locale.includes(locale));
  const socialMenu = SOCIAL_MENU.filter((i) => i.locale.includes(locale));
  const [keyDownload, setKeyDownload] = useState('before'); // Ключ нужен что бы заново отрисовать кнопку и убрать навешанные события
  const countRowsInMainMenu = Math.ceil(mainMenu.length / HALF_A_LIST + MENU_COLUMN_ITEM_DIFFERENCE);

  let logoWidth = '128';

  switch (locale) {
    case 'ru':
      switch (true) {
        case isFullhd:
          logoWidth = '159';
          break;
        case isLaptop:
          logoWidth = '172';
          break;
        default:
          logoWidth = '140';
      }
      break;
    default:
      switch (true) {
        case isFullhd:
          logoWidth = '145';
          break;
        case isLaptop:
          logoWidth = '157';
          break;
        default:
          logoWidth = '128';
      }
  }

  useEffect(() => {
    setIsInnerPage(pathname !== '/');
  }, [pathname]);

  useEffect(() => {
    setKeyDownload('after');
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkLaptop = window.matchMedia(`(min-width: 1440px)`);
      setIsLaptop(checkLaptop.matches);

      const checkFullhd = window.matchMedia(`(min-width: 1960px)`);
      setIsFullhd(checkFullhd.matches);

      const handleChange = (e) => {
        setIsFullhd(e.matches);
        setIsLaptop(e.matches);
      };
      checkFullhd.addEventListener('change', handleChange);
      checkLaptop.addEventListener('change', handleChange);

      return () => {
        checkLaptop.removeEventListener('change', handleChange);
        checkFullhd.removeEventListener('change', handleChange);
      };
    }
  }, []);

  return (
    <Container isInnerPage={isInnerPage}>
      <Wrapper>
        <Navigation>
          <NavigationMenu countRowsInMainMenu={countRowsInMainMenu}>
            <NoSSR>
              <ul>
                {mainMenu.slice(0, countRowsInMainMenu - 1).map(({ href, key, link }) => (
                  <MenuItem key={key}>
                    <CustomLink href={href} link={link && link(locale)} translateKey={key} useHtmlTranslateKey />
                  </MenuItem>
                ))}
              </ul>
              <ul>
                {mainMenu.slice(countRowsInMainMenu - 1).map(({ href, key, link }) => (
                  <MenuItem key={key}>
                    <CustomLink href={href} link={link && link(locale)} translateKey={key} useHtmlTranslateKey />
                  </MenuItem>
                ))}
                <MenuItem>
                  <SupportItem>
                    {translations.t('site.footer.support')}
                    <br />
                    <a href="mailto:support@planoplan.com">support@planoplan.com</a>
                  </SupportItem>
                </MenuItem>
              </ul>
            </NoSSR>
          </NavigationMenu>
          <LegalInformation>
            <MenuTitle>{translations.t('site.footer.legal_menu_title')}</MenuTitle>
            <ul>
              {legalDocsMenu.map(({ href, key, link }) => (
                <MenuItem key={key}>
                  <CustomLink href={href} link={link && link(locale)} translateKey={key} useHtmlTranslateKey />
                </MenuItem>
              ))}
            </ul>
          </LegalInformation>
          <DownloadMenu>
            <MenuTitle>{translations.t('site.footer.software_menu_title')}</MenuTitle>
            <ul>
              {softwareMenu.map(({ href, key, option }) => (
                <MenuItem key={key}>
                  <CustomLink
                    key={keyDownload}
                    href={href}
                    link={option && option(locale)}
                    translateKey={key}
                    useHtmlTranslateKey
                    onClick={option && option(locale) && handleClickDownload}
                    onContextMenu={option && option(locale) && downloadPlanoplanContextHandler}
                    onAuxClick={option && option(locale) && downloadPlanoplanAuxHandler}
                  />
                </MenuItem>
              ))}
            </ul>
          </DownloadMenu>
          <SocialMenu>
            <MenuTitle>{translations.t('site.footer.social_menu_title')}</MenuTitle>
            <ul>
              {socialMenu.map(({ name, icon, href, ariaLabel }) => (
                <SocialIcon href={href} key={name} target="_blank" aria-label={translations.t(ariaLabel)}>
                  <img src={getImage(icon)} alt="" height="25" />
                </SocialIcon>
              ))}
            </ul>
          </SocialMenu>
        </Navigation>
        <Branding>
          <IconLogo>
            {/* Такой href нужен для того, чтобы на страницах с PRIMARY_LANG ссылка на главную была без локали */}
            {/*<Link href="/" locale={locale === PRIMARY_LANG.value ? DEFAULT_LOCALE : locale} passHref>*/}
            <Link href="/" locale={locale} passHref>
              <Icon aria-label={locale === 'ru' ? 'Планоплан — на главную' : 'Planoplan — home'}>
                <ILogo rebrand="true" locale={locale} height={isLaptop ? 44 : 35} width={logoWidth} />
              </Icon>
            </Link>
          </IconLogo>
          <Copyright>© 2011 — {copyrightDate}</Copyright>
        </Branding>
      </Wrapper>
    </Container>
  );
};

const Container = styled.footer`
  position: relative;
  z-index: ${zIndex('footer')}; // на галереи меняется цвет страницы
  background-color: ${Colors.main_black};
  padding: 65px 16px 60px;
  flex: 0 0 auto;

  @media ${Points.tablet_portrait} {
    padding-bottom: ${Rhytm.base(20)};
  }

  @media ${Points.tablet_landscape} {
    padding: 70px 16px 70px;
  }

  @media ${Points.laptop_wide} {
    padding: 90px 16px 90px;
  }
`;

const Wrapper = styled.div`
  @media ${Points.tablet_portrait} {
    max-width: 1440px;
    margin: auto;
  }
`;

const IconLogo = styled.div`
  @media ${Points.tablet_landscape} {
    display: flex;
  }
`;

const Icon = styled.a`
  display: block;

    svg {
      display: block;

      path:not(:first-of-type) {
        fill: ${Colors.main_light_beige};
      }
    }

    &:hover {
      opacity: ${(p) => !p.isMobile && 0.8};
    }
  }
`;

const Navigation = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 40px;

  @media ${Points.tablet_landscape} {
    width: ${TABLET_CONTENT_WIDTH};
    gap: 28px;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: nowrap;
    margin: 0 auto 40px;
  }

  @media ${Points.laptop} {
    width: ${LAPTOP_CONTENT_WIDTH};
  }

  @media ${Points.laptop_wide} {
    width: ${DESKTOP_CONTENT_WIDTH};
    gap: 100px;
    margin-bottom: 50px;
  }

  @media ${Points.fullhd} {
    width: ${FULLHD_CONTENT_WIDTH};
    gap: 110px;
  }
`;

const NavigationMenu = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  ul {
    ${Mixins.resetList()};
  }

  @media ${Points.tablet_landscape} {
    width: 300px;
    max-width: 300px;
    gap: 28px;
    margin-top: 5px;
  }

  @media ${Points.laptop_wide} {
    width: 355px;
    max-width: 355px;
  }

  @media ${Points.fullhd} {
    width: 405px;
    max-width: 405px;
  }
`;

// Не <h*> — это подпись колонки, а не заголовок раздела документа, и она нигде
// не продолжает иерархию h1-h5 (Lighthouse: heading-order)
const MenuTitle = styled.p`
  ${textSize('footer_header')};
  margin: 0;
  margin-bottom: 16px;
  color: #676767;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const LegalInformation = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 35px;

  ul {
    ${Mixins.resetList()};
  }

  @media ${Points.tablet_landscape} {
    margin-top: 0;
  }
`;

const DownloadMenu = styled.div`
  ${Mixins.resetList()};
  margin-top: 35px;

  ul {
    ${Mixins.resetList()};
  }

  @media ${Points.tablet_landscape} {
    margin-top: 0;
    width: 178px;
  }

  @media ${Points.laptop_wide} {
    columns: 1;

    li:nth-child(1) {
      display: block;
    }
  }

  @media ${Points.fullhd} {
    width: 200px;
  }
`;

const SocialMenu = styled.div`
  margin-top: 35px;

  ul {
    ${Mixins.resetList()};
    flex-grow: 1;
    display: flex;
    flex-wrap: wrap;
    margin-top: 16px;
    gap: 15px;
  }

  @media ${Points.tablet_portrait} {
    flex-wrap: wrap;
    flex-grow: 0;
    justify-content: flex-end;
    align-content: start;
  }

  @media ${Points.tablet_landscape} {
    margin-top: 0px;
    width: 150px;
    max-width: 150px;
  }
`;

const MenuItem = styled.li`
  ${textSize('footer_txt')};
  margin-bottom: 18px;

  @media ${Points.tablet_landscape} {
    margin-bottom: 20px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  a {
    ${Mixins.transition('color')};
    ${Mixins.resetLink()};
    cursor: pointer;
    color: ${Colors.main_beige};

    &:hover {
      color: ${Colors.main_beige};
      border-bottom: 1px solid ${Colors.main_beige};
    }

    &:active {
      color: ${Colors.white};
      border-bottom: 1px solid ${Colors.white};
    }
  }
`;

const SocialIcon = styled.a`
  width: auto;
  max-width: 35px;
  height: 24px;
  max-height: 24px;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    filter: brightness(1.2);
    transform: translateY(-1px);
  }
}
`;

const SupportItem = styled.p`
  color: ${Colors.main_beige};
  line-height: 1.3;

  & > a {
    ${Mixins.transition('color')};
    ${Mixins.resetLink()};
    cursor: pointer;
    color: ${Colors.second_link};

    &:hover {
      color: ${Colors.main_blue};
      border-bottom: none;
    }
  }
`;

const Copyright = styled.div`
  ${textSize('description')};
  font-weight: 300;
  color: ${Colors.light_gray};

  @media ${Points.tablet_portrait} {
    display: block;
  }
`;

const Branding = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${Points.tablet_landscape} {
    width: ${TABLET_CONTENT_WIDTH};
    margin: 0 auto;
  }

  @media ${Points.laptop} {
    width: ${LAPTOP_CONTENT_WIDTH};
  }

  @media ${Points.laptop_wide} {
    width: ${DESKTOP_CONTENT_WIDTH};
  }

  @media ${Points.fullhd} {
    width: ${FULLHD_CONTENT_WIDTH};
  }
`;
